import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, Stack, Chip, Button, Divider,
  Alert, CircularProgress, Paper, Grid,
} from '@mui/material';
import {
  ArrowBack, OpenInNew, Timer, Star,
  CheckCircle, Bookmark, BookmarkBorder,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useCatalog } from '../../context/CatalogContext';
import { usePaths } from '../../context/PathsContext';

function stripHtml(html) {
  return (html || '').replace(/<[^>]*>/g, '').trim();
}

const LEVEL_COLORS = {
  beginner:     { bg: '#dcfce7', color: '#166534' },
  intermediate: { bg: '#fef9c3', color: '#854d0e' },
  advanced:     { bg: '#fee2e2', color: '#991b1b' },
};

export default function ModuleDetailPage() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { modules, loadModules, loading } = useCatalog();
  const { paths, addModule } = usePaths();
  const [addedTo, setAddedTo] = useState([]);

  useEffect(() => { if (modules.length === 0) loadModules(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const uid = decodeURIComponent(moduleId || '');
  const module = modules.find((m) => m.uid === uid);

  if (loading && modules.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 12 }}><CircularProgress /></Box>;
  }
  if (!module) {
    return (
      <Container sx={{ py: 6, textAlign: 'center' }}>
        <Alert severity="error">Module not found. <Button onClick={() => navigate('/modules')}>Browse all</Button></Alert>
      </Container>
    );
  }

  const handleAddToPath = (pathId) => {
    addModule(pathId, { uid: module.uid, title: module.title, type: 'module', duration_in_minutes: module.duration_in_minutes });
    setAddedTo((prev) => [...new Set([...prev, pathId])]);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 3 }}>Back</Button>

      <Grid container spacing={4}>
        {/* Main content */}
        <Grid item xs={12} md={8}>
          {/* Header */}
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip label="Module" size="small" sx={{ backgroundColor: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }} />
            {(module.levels || []).map((l) => (
              <Chip key={l} label={l.charAt(0).toUpperCase() + l.slice(1)} size="small"
                sx={{ ...LEVEL_COLORS[l.toLowerCase()] || { bg: '#f3f4f6', color: '#374151' }, fontWeight: 600 }} />
            ))}
          </Stack>

          <Typography variant="h3" fontWeight={800} letterSpacing="-1.5px" sx={{ mb: 2, lineHeight: 1.15, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
            {module.title}
          </Typography>

          <Stack direction="row" spacing={3} sx={{ mb: 3 }} flexWrap="wrap">
            {module.duration_in_minutes && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Timer sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {Math.round(module.duration_in_minutes / 60 * 10) / 10}h
                  {module.number_of_children ? ` · ${module.number_of_children} units` : ''}
                </Typography>
              </Stack>
            )}
            {module.rating?.average && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Star sx={{ fontSize: 16, color: '#f59e0b' }} />
                <Typography variant="body2">{module.rating.average.toFixed(1)} ({module.rating.count?.toLocaleString()} ratings)</Typography>
              </Stack>
            )}
          </Stack>

          <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#374151', mb: 4, fontSize: '1.05rem' }}>
            {stripHtml(module.summary)}
          </Typography>

          <Divider sx={{ mb: 4 }} />

          {/* Products */}
          {(module.products || []).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Products covered</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                {module.products.map((p) => (
                  <Chip key={p} label={p} size="small" sx={{ backgroundColor: '#f3f4f6' }} />
                ))}
              </Stack>
            </Box>
          )}

          {/* Roles */}
          {(module.roles || []).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Target roles</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                {module.roles.map((r) => (
                  <Chip key={r} label={r.replace(/-/g, ' ')} size="small" sx={{ backgroundColor: '#f3f4f6', textTransform: 'capitalize' }} />
                ))}
              </Stack>
            </Box>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Action card */}
          <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e5e7eb', mb: 3 }}>
            {module.social_image_url ? (
              <Box component="img" src={module.social_image_url} alt="" sx={{ width: '100%', borderRadius: '10px', mb: 2 }} />
            ) : module.icon_url ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box component="img" src={module.icon_url} alt="" sx={{ width: 80, height: 80 }} />
              </Box>
            ) : null}

            <Button
              fullWidth variant="contained" size="large"
              endIcon={<OpenInNew />}
              href={module.url} target="_blank" rel="noopener noreferrer"
              sx={{ borderRadius: '10px', fontWeight: 700, mb: 2 }}
            >
              Start on Microsoft Learn
            </Button>

            {paths.length > 0 && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="caption" color="text.disabled">Add to my paths</Typography>
                </Divider>
                <Stack spacing={1}>
                  {paths.map((p) => {
                    const done = addedTo.includes(p.id) || (p.modules || []).some((m) => m.uid === module.uid);
                    return (
                      <Button
                        key={p.id}
                        fullWidth variant="outlined" size="small"
                        startIcon={done ? <CheckCircle /> : <BookmarkBorder />}
                        onClick={() => handleAddToPath(p.id)}
                        disabled={done}
                        sx={{ borderRadius: '8px', justifyContent: 'flex-start', textTransform: 'none' }}
                      >
                        {p.title}
                      </Button>
                    );
                  })}
                </Stack>
              </>
            )}

            <Button
              fullWidth variant="text" sx={{ mt: 2, fontWeight: 600 }}
              startIcon={<Bookmark />}
              onClick={() => navigate('/create-path')}
            >
              Create new path
            </Button>
          </Paper>

          {/* Meta */}
          <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Details</Typography>
            {module.last_modified && (
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Last updated</Typography>
                <Typography variant="caption">{new Date(module.last_modified).toLocaleDateString()}</Typography>
              </Stack>
            )}
            {module.locale && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">Language</Typography>
                <Typography variant="caption" textTransform="uppercase">{module.locale}</Typography>
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
